import {
  AssignedPropertyValue,
  AssignedValueType,
  Property,
  PropertyType,
  PropertyValue as PropertyValueType,
} from 'api/clients/PropertyClient'
import { DateFormat } from 'common/PropertyForm/Types/Date'
import { NumberFormat } from 'common/PropertyForm/Types/Number'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { displayDateTime } from 'utils/dates'

dayjs.extend(localizedFormat)
dayjs.extend(advancedFormat)

export const convertPropertyValuesToAssignedPropertyValues = (
  property: Property,
  propertyValues: PropertyValueType[],
) => {
  if (
    [
      PropertyType.SingleSelect,
      PropertyType.Text,
      PropertyType.Checkbox,
      PropertyType.Date,
      PropertyType.MultiSelect,
      PropertyType.Number,
      PropertyType.Link,
    ].includes(property.type)
  ) {
    return propertyValues.map((pv) => ({
      assigned_value_type: AssignedValueType.PropertyValue,
      assigned_value_id: pv?.id,
    })) as AssignedPropertyValue[]
  }
  if (property.type === PropertyType.Document) {
    return propertyValues.map((pv) => ({
      assigned_value_type: AssignedValueType.Article,
      assigned_value_id: pv?.id,
    })) as AssignedPropertyValue[]
  }
  if (property.type === PropertyType.Person) {
    return propertyValues.map((pv) => ({
      assigned_value_type: AssignedValueType.User,
      assigned_value_id: pv?.id,
    })) as AssignedPropertyValue[]
  }
}

export const formatDatePropertyValue = (value: string, format?: DateFormat) => {
  if (!value) return ''

  if (!format) return displayDateTime(value)

  switch (format) {
    case DateFormat.AllNumeral:
      return dayjs(value).format('L')
    case DateFormat.AllNumeralReverse:
      return dayjs(value).format('DD/MM/YYYY')
    case DateFormat.MonthName:
      return dayjs(value).format('MMMM D, YYYY')
    case DateFormat.MonthNameShort:
      return dayjs(value).format('MMM D, YYYY')
    case DateFormat.DayOfMonth:
      return dayjs(value).format('MMM Do, YYYY')
    case DateFormat.MonthAndDayName:
      return dayjs(value).format('dddd, MMMM D, YYYY')
    case DateFormat.MonthAndDayNameShort:
      return dayjs(value).format('ddd, MMM D, YYYY')
    case DateFormat.DateTime:
      return displayDateTime(value)
  }
}

export const formatNumberValue = (value: string, format?: NumberFormat) => {
  if (!value) return ''
  const isNumberTooBig = parseInt(value) > Number.MAX_SAFE_INTEGER
  let result = ''

  switch (format) {
    case NumberFormat.Integer:
      result = isNumberTooBig
        ? value
        : new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(parseInt(value))
      break
    case NumberFormat.Decimal1:
      result = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value as any)
      break
    case NumberFormat.Decimal2:
      result = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value as any)
      break
    case NumberFormat.Decimal3:
      result = new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }).format(value as any)
      break
    default:
      result = value
      break
  }
  if (result === 'NaN') return ''
  return result
}

type Empty = null | undefined

export function isEmpty(value: any): value is Empty {
  return [null, undefined].includes(value)
}

export function removeEmptyElements<T>(array: Array<T | Empty>): T[] {
  return array.filter((value) => !isEmpty(value)) as T[]
}
